<script setup lang="ts">
/**
 * TheSidebar — linkerbalk met de documentatie-navigatie van de
 * actieve hoofdcategorie (de hoofdcategorieën zelf staan in de header).
 *
 * - Inklapbaar tot een smalle rail (toestand wordt onthouden)
 * - In breedte verstelbaar: slepen aan de rechterrand, of met het
 *   toetsenbord via de greep (pijltjestoetsen, Home/End)
 *
 * De navigatieboom wordt door App.vue geladen (de header heeft hem ook
 * nodig wanneer deze sidebar verborgen is).
 */

import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-vue-next'
import { useSectionStore } from '../../stores/sections'
import SidebarNav from './SidebarNav.vue'

const sectionStore = useSectionStore()
const { t } = useI18n()

/* ── Breedte & inklappen (persistent) ───────────────────────── */
const MIN_WIDTH = 220
const MAX_WIDTH = 520
const RAIL_WIDTH = 52

const collapsed = ref(localStorage.getItem('sidebar_collapsed') === '1')
const width = ref(clamp(Number(localStorage.getItem('sidebar_width')) || 288))
const isDragging = ref(false)

watch(collapsed, v => localStorage.setItem('sidebar_collapsed', v ? '1' : '0'))
watch(width, v => localStorage.setItem('sidebar_width', String(v)))

function clamp(value: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value))
}

function toggleCollapsed() {
  collapsed.value = !collapsed.value
}

/* Slepen met muis/aanraking (pointer events) */
function startResize(event: PointerEvent) {
  event.preventDefault()
  isDragging.value = true
  const handle = event.currentTarget as HTMLElement
  handle.setPointerCapture(event.pointerId)

  const onMove = (e: PointerEvent) => {
    width.value = clamp(e.clientX)
  }
  const onUp = () => {
    isDragging.value = false
    handle.removeEventListener('pointermove', onMove)
    handle.removeEventListener('pointerup', onUp)
    handle.removeEventListener('pointercancel', onUp)
  }

  handle.addEventListener('pointermove', onMove)
  handle.addEventListener('pointerup', onUp)
  handle.addEventListener('pointercancel', onUp)
}

/* Toetsenbord-alternatief op de greep (WCAG 2.1.1) */
function onHandleKeydown(event: KeyboardEvent) {
  const step = event.shiftKey ? 48 : 16
  switch (event.key) {
    case 'ArrowLeft': width.value = clamp(width.value - step); break
    case 'ArrowRight': width.value = clamp(width.value + step); break
    case 'Home': width.value = MIN_WIDTH; break
    case 'End': width.value = MAX_WIDTH; break
    case 'Enter':
    case ' ': toggleCollapsed(); break
    default: return
  }
  event.preventDefault()
}
</script>

<template>
  <aside
    class="sidebar border-r border-gray-200 flex flex-col h-full relative"
    :class="{ 'sidebar-animated': !isDragging }"
    :style="{ width: `${collapsed ? RAIL_WIDTH : width}px` }"
    role="navigation"
    :aria-label="t('chrome.sidebar.label')"
  >
    <!-- Ingeklapte rail: alleen de uitklapknop -->
    <template v-if="collapsed">
      <div class="flex flex-col items-center p-2">
        <button
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
          :aria-label="t('chrome.sidebar.expand')"
          :title="t('chrome.sidebar.expand')"
          @click="toggleCollapsed"
        >
          <PanelLeftOpen class="w-5 h-5" aria-hidden="true" />
        </button>
      </div>
    </template>

    <!-- Uitgeklapte navigatie -->
    <template v-else>
      <!-- Inklapknop rechtsboven in de balk (het logo leeft in de header) -->
      <div class="flex items-center justify-end px-2 pt-2">
        <button
          type="button"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex-shrink-0"
          :aria-label="t('chrome.sidebar.collapse')"
          :title="t('chrome.sidebar.collapse')"
          @click="toggleCollapsed"
        >
          <PanelLeftClose class="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      <!-- Documentatieboom -->
      <nav class="flex-1 overflow-y-auto px-3 pb-4" :aria-label="t('chrome.sidebar.docs')">
        <SidebarNav :scoped="true" />
        <p v-if="sectionStore.isLoading && sectionStore.tree.length === 0" class="px-2 text-sm text-gray-400">
          {{ t('chrome.sidebar.loading') }}
        </p>
      </nav>

      <!-- Sleepgreep voor breedte (rechterrand) -->
      <div
        class="resize-handle"
        :class="{ 'resize-handle-active': isDragging }"
        role="separator"
        aria-orientation="vertical"
        :aria-label="t('chrome.sidebar.resizeHandle')"
        :aria-valuenow="width"
        :aria-valuemin="MIN_WIDTH"
        :aria-valuemax="MAX_WIDTH"
        tabindex="0"
        @pointerdown="startResize"
        @keydown="onHandleKeydown"
        @dblclick="width = 288"
      ></div>
    </template>
  </aside>
</template>

<style scoped>
.sidebar {
  background-color: #E5EBED;
}

.sidebar-animated {
  transition: width 0.2s ease;
}
@media (prefers-reduced-motion: reduce) {
  .sidebar-animated { transition: none; }
}

.resize-handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: 7px;
  height: 100%;
  cursor: col-resize;
  z-index: 10;
  touch-action: none;
}
.resize-handle::after {
  content: '';
  position: absolute;
  top: 0;
  right: 2px;
  width: 3px;
  height: 100%;
  background: transparent;
  transition: background 0.15s ease;
}
.resize-handle:hover::after,
.resize-handle:focus-visible::after,
.resize-handle-active::after {
  background: theme('colors.primary.400');
}
.resize-handle:focus-visible {
  outline: none;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: theme('colors.gray.300');
  border-radius: 3px;
}
</style>
